import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Youtube, Play, ChevronLeft } from "lucide-react";

const VideoModalContext = createContext(null);

export function VideoModalProvider({ children }) {
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);

  const openVideo = useCallback((video, videos = []) => {
    setList(videos.length ? videos : [video]);
    setCurrent(video);
  }, []);

  const close = useCallback(() => setCurrent(null), []);

  return (
    <VideoModalContext.Provider value={{ openVideo }}>
      {children}
      <ShortsTheater list={list} current={current} setCurrent={setCurrent} onClose={close} />
    </VideoModalContext.Provider>
  );
}

export const useVideoModal = () => useContext(VideoModalContext);

function ShortsTheater({ list, current, setCurrent, onClose }) {
  useEffect(() => {
    if (!current) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    // Cross-origin YouTube iframe steals keyboard focus; periodically restore it
    // to the document so Escape continues to close the theater.
    const refocus = setInterval(() => {
      if (document.activeElement && document.activeElement.tagName === "IFRAME") {
        window.focus();
      }
    }, 600);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      clearInterval(refocus);
    };
  }, [current, onClose]);

  const related = list.filter((v) => v.youtube_id !== current?.youtube_id);

  return createPortal(
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-8"
          data-testid="shorts-theater"
        >
          {/* Blurred backdrop */}
          <div className="absolute inset-0 bg-black/85 backdrop-blur-2xl" onClick={onClose} />
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#4da6ff]/12 blur-[160px] rounded-full animate-glow" />
          </div>

          <button onClick={onClose} data-testid="shorts-theater-close"
            className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full glass flex items-center justify-center text-white hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-[1] w-full max-w-6xl max-h-[92vh] overflow-y-auto grid lg:grid-cols-[minmax(0,380px)_1fr] gap-8 lg:gap-10 items-start"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Vertical player */}
            <div className="flex flex-col">
              <motion.div key={current.youtube_id} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
                className="relative mx-auto w-full max-w-[340px]">
                <div className="absolute -inset-5 bg-[#4da6ff]/20 blur-[50px] rounded-[2rem]" />
                <div className="relative glass-card overflow-hidden p-2" style={{ aspectRatio: "9/16" }}>
                  <iframe
                    key={current.youtube_id}
                    className="w-full h-full rounded-[1.1rem]"
                    src={`https://www.youtube.com/embed/${current.youtube_id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                    title={current.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="shorts-theater-player"
                  />
                </div>
              </motion.div>
              <div className="mt-5 text-center lg:text-left max-w-[340px] mx-auto lg:mx-0">
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#4da6ff]">{current.category}</span>
                <h3 className="font-head text-xl font-medium mt-1.5 leading-snug">{current.title}</h3>
                {current.description && <p className="text-zinc-500 text-sm mt-2 font-light line-clamp-2">{current.description}</p>}
                <a href={`https://www.youtube.com/watch?v=${current.youtube_id}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 mt-4 text-xs text-zinc-500 hover:text-[#4da6ff] transition-colors">
                  <Youtube className="w-4 h-4" /> Open on YouTube
                </a>
              </div>
            </div>

            {/* Up Next */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-5">
                <Play className="w-4 h-4 text-[#4da6ff]" fill="currentColor" />
                <h4 className="font-head text-lg font-medium">Up Next</h4>
                <span className="text-xs text-zinc-600">· keep watching</span>
              </div>
              {related.length === 0 ? (
                <p className="text-zinc-600 text-sm font-light">No more Shorts right now — explore the rest of the channel.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="shorts-theater-related">
                  {related.map((v) => (
                    <button key={v.youtube_id} onClick={() => setCurrent(v)} data-testid={`shorts-related-${v.youtube_id}`}
                      className="group flex gap-3 p-2.5 rounded-2xl border border-white/5 hover:border-white/15 hover:bg-white/[0.03] transition-all text-left">
                      <div className="relative w-24 shrink-0 rounded-xl overflow-hidden" style={{ aspectRatio: "16/10" }}>
                        <img src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Play className="w-5 h-5 text-white" fill="white" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-[#4da6ff]">{v.category}</span>
                        <p className="text-sm text-zinc-200 leading-snug line-clamp-2 mt-0.5">{v.title}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
